/**
 * Application configuration constants
 */

/**
 * Local BIM Comply API is HTTPS-only (self-signed dev certificate), so the
 * local override must use `https`. Overridable via REACT_APP_LOCAL_API_HOST,
 * matching the REACT_APP_* override pattern used for oidc config in
 * `src/index.tsx`.
 */
const LOCAL_API_HOST = process.env.REACT_APP_LOCAL_API_HOST || 'https://localhost:8080';

/**
 * Get API host based on environment
 */
export function getApiHost(address?: string | null): string {
  return address === "local" ? LOCAL_API_HOST : 'https://api.bimcomply.com';
}

/**
 * Local BIM Comply API issues its own OIDC tokens (distinct issuer/signing
 * key from production), so the authority used to authenticate must be
 * derived from the same `address` signal as `getApiHost`. Overridable via
 * REACT_APP_LOCAL_AUTHORITY; defaults to the local API host itself, since
 * the local API is also the local token issuer.
 */
const LOCAL_AUTHORITY = process.env.REACT_APP_LOCAL_AUTHORITY || LOCAL_API_HOST;
const PRODUCTION_AUTHORITY = process.env.REACT_APP_AUTHORITY || 'https://api.bimcomply.com';

/**
 * Get the OIDC authority (token issuer) that corresponds to the same
 * environment selected by `address`. Keeping this derived from the exact
 * same `address` value that `getApiHost` uses ensures authentication and
 * API requests always target the same environment and never silently mix
 * a production-issued token with the local API (or vice versa).
 */
export function getAuthority(address?: string | null): string {
  return address === "local" ? LOCAL_AUTHORITY : PRODUCTION_AUTHORITY;
}

/**
 * The `address` query param as seen on the initial page load. The OIDC
 * client (`AuthProvider`) is constructed once, before the router mounts, so
 * it must read `address` directly from `window.location` rather than from
 * `useSearchParams` (which is only available after routing starts).
 */
export const INITIAL_ADDRESS =
  typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('address')
    : null;

/**
 * The authority the running `AuthProvider` instance was actually
 * initialized with. Used to detect, at runtime, when the current URL's
 * `address` no longer matches the environment the app authenticated
 * against (e.g. the user navigated client-side to a different `address`
 * without a full page reload).
 */
export const ACTIVE_AUTHORITY = getAuthority(INITIAL_ADDRESS);

/**
 * Default localStorage keys
 */
export const STORAGE_KEYS = {
  LANGUAGE: 'language',
  LEGISLATION_COUNTRY: 'legislationCountry',
  VALIDATOR: 'validator',
  EXCLUDED_DOCUMENTS: 'excludedDocuments',
  CHAT_MODE: 'chatMode',
  DEBUG_MODE: 'debugMode',
} as const;

/**
 * API endpoints
 */
export const API_ENDPOINTS = {
  VALIDATOR: '/api/v1/BIMValidator',
  VALIDATOR_FINDINGS_V2: '/api/v2/BIMValidator/findings',
  CHAT: '/api/v1/BIMChat',
  FEEDBACK: '/api/v1/user-feedback',
  AVAILABLE_COUNTRIES: '/api/v1/bimvalidator/available-countries',
  GEOCODE_COORDINATES: '/api/v1/geocode/address',
  LEGISLATION: '/api/v1/legislation-documents',
  ELEMENT_TYPE: '/api/v1/element-type',
} as const;

/**
 * Default headers for API requests
 */
export const DEFAULT_HEADERS = {
  "Access-Control-Allow-Origin": "*",
  "Content-Type": "application/json",
} as const;

/**
 * Get authorization headers with token
 */
export function getAuthHeaders(token?: string): Record<string, string> {
  return {
    ...DEFAULT_HEADERS,
    ...(token && { "Authorization": `Bearer ${token}` })
  };
}

/**
 * Default settings values
 */
export const DEFAULT_SETTINGS = {
  LANGUAGE: 'English (Ireland)',
  LEGISLATION_COUNTRY: 'Ireland',
  VALIDATOR: 'Default',
  CHAT_MODE: 'legislation' as const,
  // Opt-in only: validation debug capture must default OFF for every user.
  DEBUG_MODE: false,
} as const;

/**
 * Available language options
 */
export const AVAILABLE_LANGUAGES = [
  'English (Ireland)',
  'English (United Kingdom)',
  'Portuguese (PT)',
] as const;

/**
 * Available validator options with descriptions
 */
export const AVAILABLE_VALIDATORS = [
  { value: 'Fast', description: 'Best option for fast validations' },
  { value: 'Default', description: 'Best option for quality validations' },
] as const;
